import { useEffect, useMemo, useRef } from "react";
import { useSelector, shallowEqual, useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { setOnBoardingUI } from "actions/user";
import { useLocation } from "react-router-dom/cjs/react-router-dom.min";

const useOnboardingData = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const timeoutRef = useRef(null);
    const location = useLocation();

    const { onboardingData, onboardingUI, hideLoginDialog } = useSelector(
        (state) => ({
            onboardingData: state.user?.onBoardingData,
            onboardingUI: state.user?.onboardingUI,
            hideLoginDialog: state.user?.hideLoginDialog ?? false,
        }),
        shallowEqual
    );

    const hasEmailVerified = Boolean(onboardingData?.emailVerified);
    const hasIdentification = Boolean(onboardingData?.ref);
    const hasPWARegister = Boolean(onboardingData?.onboardingComplete);
    const isOnboardingNull = onboardingData == null;

    function removeRoomAdded() {
        if (onboardingUI?.room) {
            dispatch(
                setOnBoardingUI({
                    showDialog: true,
                    room: null,
                    textDialog: t("sign-in-and-enjoy-all-our-services"),
                    action: "removing-room-added",
                })
            );
        }
    }

    function showRoomAddedToast() {
        if (!onboardingUI?.room) return;

        toast(t("room added", { room: onboardingUI.room }), {
            autoClose: 10000,
            closeOnClick: false,
            onClose: () => {
                removeRoomAdded();
                if (timeoutRef.current) {
                    clearTimeout(timeoutRef.current);
                    timeoutRef.current = null;
                }
            },
        });

        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        timeoutRef.current = setTimeout(() => {
            removeRoomAdded();
            timeoutRef.current = null;
        }, 10500);
    }

    useEffect(() => {
        if (
            onboardingUI?.room &&
            onboardingUI?.roomFromParams &&
            location.pathname === "/site" &&
            sessionStorage.getItem("roomAddedFromParams") === "true"
        ) {
            showRoomAddedToast();
            sessionStorage.removeItem("roomAddedFromParams");
        }
    }, [onboardingUI?.room, onboardingData?.roomFromParams, sessionStorage.getItem("roomAddedFromParams")]);

    return {
        obData: onboardingData,
        obOnComplete: onboardingData?.onboardingComplete ?? false,
        hasEmailVerified,
        hasIdentification,
        hasPWARegister,
        isOnboardingNull,
        showObDialog: onboardingUI?.showDialog ?? false,
        showObText: onboardingUI?.textDialog ?? null,
        showNotif: onboardingUI?.showNotif ?? false,
        hideLoginDialog,
        onboardingUI,
        showToast: { roomAdded: showRoomAddedToast },
        removeRoomAdded,
    };
};

export default useOnboardingData;
